import { useCallback, useMemo, useRef, useState } from "react";
import {
  CustomError,
  ValidateMsg,
  Validate,
} from "api/interfaces/commonInterface.interface";
import {
  PostResellerSignUpRequest,
  PostResellerSignUpResponse,
  PostResellerSubmissionRequest,
  PostResellerSubmissionResponse,
  ResellerUserInfo,
  RESSELLER_USER_TYPE,
  SIGNUP_STEP,
  SIGNUP_UPDATE_TYPE,
  SignupUserInfo,
} from "api/interfaces/authInterface.interface";
import * as s from "../SignupStyled";
import * as cs from "../../SignCommonStyled";
import { UnderlineText, ValidateWord } from "components/atoms/text/Text";
import BigButton from "components/atoms/buttons/BigButton";
import useStatusMutation from "hook/useStatusMutation";
import { checkOffsetTimezone } from "utils/timeUtil";
import * as option from "utils/options";
import useApiError from "hook/useApiError";
import {
  checkPasswordNumberRegEx,
  checkPasswordRegEx,
  orgNameAlphabetRule,
  orgNameRule,
} from "utils/regEx";
import { isPhoneNumberValid } from "utils/validHelper";
import InputItem from "../common/InputItem";
import Password from "../common/Password";
import Phone from "../common/Phone";
import Agreement from "../common/Agreement";
import OrgName from "../common/OrgName";
import { postResellerSignUp, postResellerSubmission } from "api/authAPI";
import { COLORS } from "styles/colors";
import { useNavigate } from "react-router-dom";
import { handleDuplicateAccountName } from "utils/AccountUtil";
import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import { EULAModal } from "pages/terms/text/EULAModal";
import { SLAModal } from "pages/terms/text/SLAModal";
import { ButtonWrapper } from "components/templates/modal/ModalTemplateStyled";
import TermsModal from "pages/terms/text/TermsModal";
import { checkEmpty, toTrim } from "utils/functions";

interface Props {
  checkedEmail: string;
  onClickVerify: () => void;
  onClickVerifyCancel: () => void;
  verifyCode: string;
  onChangeVerifyCode: (value: string) => void;
  passwordRef: React.RefObject<HTMLInputElement>;
  confirmPasswordRef: React.RefObject<HTMLInputElement>;
  validateMsg: string;
  onClickResend: () => void;
  isValidVerify: boolean;
  isPhoneVerified: boolean;
  onSendVerify: () => void;
  mfaNumber: string;
  validateInputMsg: ValidateMsg;
  step: SIGNUP_STEP;
  userInfo: SignupUserInfo;
  onChangeUserInfo: (type: SIGNUP_UPDATE_TYPE, data: string | boolean) => void;
  setStep: (step: SIGNUP_STEP) => void;
  setIsValidated: React.Dispatch<React.SetStateAction<Validate>>;
  setValidateInputMsg: React.Dispatch<React.SetStateAction<ValidateMsg>>;
  setValidateMsg: React.Dispatch<React.SetStateAction<string>>;
  isVerifyPhoneNumber: boolean;
}

const initialUserInfo: ResellerUserInfo = {
  firstName: "",
  lastName: "",
  password: "",
  confirmPassword: "",
  phone: "",
  companyName: "",
  email: "",
  timezone: "",
  dateFormat: "",
  timeFormat: "",
  verifyPhone: false,
  slaAgreed: false,
  agreement: false,
};

export default function Reseller(props: Props): JSX.Element {
  const {
    checkedEmail,
    onClickVerify,
    onClickVerifyCancel,
    verifyCode,
    onChangeVerifyCode,
    passwordRef,
    confirmPasswordRef,
    validateMsg,
    onClickResend,
    isValidVerify,
    isPhoneVerified,
    onSendVerify,
    mfaNumber,
    validateInputMsg,
    step,
    onChangeUserInfo,
    setStep,
    isVerifyPhoneNumber,
    setIsValidated,
    setValidateInputMsg,
    setValidateMsg,
  } = props;
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const agreementRef = useRef<HTMLInputElement>(null);
  const companyNameRef = useRef<HTMLInputElement>(null);
  const [resellerSignupInfo, setResellerSignupInfo] =
    useState<ResellerUserInfo>({
      ...initialUserInfo,
    });
  const navigate = useNavigate();

  const userInfo = useMemo(() => {
    const resellerCompanyName = (
      props.userInfo as SignupUserInfo & {
        companyName?: string;
      }
    ).companyName;

    return {
      ...resellerSignupInfo,
      firstName: props.userInfo.firstName,
      lastName: props.userInfo.lastName,
      password: props.userInfo.password,
      confirmPassword: props.userInfo.confirmPassword,
      phone: props.userInfo.phoneNumber,
      companyName: resellerCompanyName || resellerSignupInfo.companyName,
      email: checkedEmail,
      verifyPhone: props.userInfo.verifyPhone,
      agreement: props.userInfo.agreement,
    };
  }, [checkedEmail, props.userInfo, resellerSignupInfo]);
  const { intlString, lang, notifyError } = useApiError();
  const handleResellerError = (err: CustomError) => {
    if (err.response?.data.error === 3008) {
      handleDuplicateAccountName({
        setFn: setValidateInputMsg,
        validateKey: "companyName",
        elementId: "dw_signup_input_org_name",
        message: intlString(lang.ServerErrorCode[3008]),
      });
      return;
    }
    notifyError(intlString(lang.validateMsg.signup.resellerSignupFail));
  };
  const mutationResellerSignUp = useStatusMutation(postResellerSignUp, {
    onSuccess: (res: PostResellerSignUpResponse) => {
      if (!res.result || res.error !== 0) {
        throw new Error(res.errorString);
      }
      setStep(SIGNUP_STEP.RESELLER_SIGNUP_SUCCESS);
    },
    onError: handleResellerError,
  });
  const mutationResellerSubmission = useStatusMutation(postResellerSubmission, {
    onSuccess: (res: PostResellerSubmissionResponse) => {
      if (!res.result || res.error !== 0) {
        throw new Error(res.errorString);
      }
      setStep(SIGNUP_STEP.RESELLER_SUBMISSION_SUCCESS);
    },
    onError: handleResellerError,
  });
  const onClickBackLogin = useCallback(() => {
    navigate("/login", {
      state: {
        email: checkedEmail,
      },
    });
  }, []);
  const onClickSignup = useCallback(() => {
    if (!mutationResellerSignUp.idle) {
      return;
    }
    setIsValidated({
      firstName: false,
      lastName: false,
      password: false,
      confirmPassword: false,
      phoneNumber: false,
      agreement: false,
      companyName: false,
    });
    setValidateInputMsg({
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
      phoneNumber: "",
      agreement: "",
      companyName: "",
    });
    let isChecked = false;
    let info: PostResellerSignUpRequest | PostResellerSubmissionRequest = {
      email: toTrim(checkedEmail),
      firstName: toTrim(userInfo.firstName),
      lastName: toTrim(userInfo.lastName),
      companyName: toTrim(userInfo.companyName),
      phone: userInfo.phone,
    };
    if (step === SIGNUP_STEP.RESELLER_SIGNUP) {
      info = {
        ...info,
        password: toTrim(userInfo.password),
        verifyPhone: userInfo.verifyPhone,
        timezone:
          option.timezoneOptions.find((opt) => {
            return (
              checkOffsetTimezone(
                opt.value,
                Intl.DateTimeFormat().resolvedOptions().timeZone
              ) === opt.value
            );
          })?.value || "America/Detroit",
        dateFormat: option.dateFormatOptions[2].value,
        timeFormat: option.timeFormatOptions[1].value,
      };
    }

    if (checkEmpty(info.firstName)) {
      setIsValidated((prev) => ({
        ...prev,
        firstName: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        firstName: intlString(lang.validateMsg.signup.firstname),
      }));
      firstNameRef.current?.focus();
      isChecked = true;
    }
    if (checkEmpty(info.lastName)) {
      setIsValidated((prev) => ({
        ...prev,
        lastName: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        lastName: intlString(lang.validateMsg.signup.lastname),
      }));
      lastNameRef.current?.focus();
      isChecked = true;
    }
    if (step === SIGNUP_STEP.RESELLER_SIGNUP) {
      if (checkEmpty((info as PostResellerSignUpRequest).password)) {
        setIsValidated((prev) => ({
          ...prev,
          password: true,
        }));
        setValidateInputMsg((prev) => ({
          ...prev,
          password: intlString(lang.validateMsg.signup.password.empty),
        }));
        passwordRef.current?.focus();
        isChecked = true;
      } else if (
        userInfo.password.length < 8 ||
        !checkPasswordRegEx(userInfo.password) ||
        !checkPasswordNumberRegEx(userInfo.password)
      ) {
        setIsValidated((prev) => ({
          ...prev,
          password: true,
        }));
        setValidateInputMsg((prev) => ({
          ...prev,
          password: intlString(
            lang.validateMsg.signup.reSetPassword.notRequired
          ),
        }));
        passwordRef.current?.focus();
        isChecked = true;
      }

      if (checkEmpty(userInfo.confirmPassword)) {
        setIsValidated((prev) => ({
          ...prev,
          confirmPassword: true,
        }));
        setValidateInputMsg((prev) => ({
          ...prev,
          confirmPassword: intlString(lang.validateMsg.signup.password.empty),
        }));
        confirmPasswordRef.current?.focus();
        isChecked = true;
      } else if (userInfo.password !== userInfo.confirmPassword) {
        setIsValidated((prev) => ({
          ...prev,
          confirmPassword: true,
        }));
        setValidateInputMsg((prev) => ({
          ...prev,
          confirmPassword: intlString(lang.validateMsg.signup.passwordMismatch),
        }));
        confirmPasswordRef.current?.focus();
        isChecked = true;
      }
    }
    if (checkEmpty(info.companyName)) {
      setIsValidated((prev) => ({
        ...prev,
        companyName: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        companyName: intlString(lang.validateMsg.signup.companyName),
      }));
      companyNameRef.current?.focus();
      isChecked = true;
    }
    if (
      !(info.companyName.length >= 3 && info.companyName.length <= 100) ||
      orgNameRule(info.companyName) ||
      orgNameAlphabetRule(info.companyName)
    ) {
      setIsValidated((prev) => ({
        ...prev,
        companyName: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        companyName: intlString(lang.validateMsg.signup.orgName),
      }));
      companyNameRef.current?.focus();
      isChecked = true;
    }
    if (checkEmpty(info.phone) && step === SIGNUP_STEP.RESELLER_SIGNUP) {
      setIsValidated((prev) => ({
        ...prev,
        phoneNumber: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        phoneNumber: intlString(lang.validateMsg.signup.phone.empty),
      }));
      isChecked = true;
      //핸드폰번호는 입력되었는데 인증번호 요청을 하지 않았을때
    }
    if (!checkEmpty(info.phone) && !isPhoneNumberValid(info.phone)) {
      setIsValidated((prev) => ({
        ...prev,
        phoneNumber: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        phoneNumber: intlString(lang.validateMsg.profile.phone.validate),
      }));
      isChecked = true;
    } else if (
      !checkEmpty(info.phone) &&
      !verifyCode &&
      step === SIGNUP_STEP.RESELLER_SIGNUP
    ) {
      setIsValidated((prev) => ({
        ...prev,
        phoneNumber: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        phoneNumber: intlString(lang.validateMsg.signup.requiredVerifyCode),
      }));
      isChecked = true;
    }
    //인증번호 요청은 했으나 인증이 되지 않았을 때
    else if (
      !checkEmpty(info.phone) &&
      !isVerifyPhoneNumber &&
      step === SIGNUP_STEP.RESELLER_SIGNUP
    ) {
      setIsValidated((prev) => ({
        ...prev,
        phoneNumber: true,
      }));
      setValidateInputMsg((prev) => ({
        ...prev,
        phoneNumber: intlString(lang.validateMsg.signup.requiredVerifyNumber),
      }));
      isChecked = true;
    }

    if (
      (!userInfo.agreement || !userInfo.slaAgreed) &&
      step === SIGNUP_STEP.RESELLER_SIGNUP
    ) {
      setIsValidated((prev) => ({
        ...prev,
        agreement: true,
      }));
      setValidateMsg(intlString(lang.validateMsg.signup.agreement));
      agreementRef.current?.focus();
      isChecked = true;
    }
    if (isChecked) {
      return;
    }
    if (step === SIGNUP_STEP.RESELLER_SUBMISSION) {
      mutationResellerSubmission.mutate(info);
      return;
    }
    mutationResellerSignUp.mutate(info);
  }, [
    mutationResellerSignUp,
    setIsValidated,
    setValidateInputMsg,
    checkedEmail,
    userInfo,
    step,
    verifyCode,
    isVerifyPhoneNumber,
    intlString,
    lang,
    passwordRef,
    confirmPasswordRef,
    setValidateMsg,
    mutationResellerSubmission,
  ]);

  const onChangeResellerInfo = (
    type: SIGNUP_UPDATE_TYPE,
    data: string | boolean,
    isProps: boolean = false
  ) => {
    if (
      type === SIGNUP_UPDATE_TYPE.AGREEMENT ||
      type === SIGNUP_UPDATE_TYPE.FIRST_NAME ||
      type === SIGNUP_UPDATE_TYPE.LAST_NAME ||
      type === SIGNUP_UPDATE_TYPE.PASSWORD ||
      type === SIGNUP_UPDATE_TYPE.CONFIRM_PASSWORD ||
      type === SIGNUP_UPDATE_TYPE.PHONE_NUMBER
    ) {
      onChangeUserInfo(type as unknown as SIGNUP_UPDATE_TYPE, data);
    } else if (
      type === SIGNUP_UPDATE_TYPE.ORG_NAME ||
      type === SIGNUP_UPDATE_TYPE.SLA_AGREED
    ) {
      setResellerSignupInfo((prev) => {
        const key =
          type === SIGNUP_UPDATE_TYPE.ORG_NAME ? "companyName" : "slaAgreed";
        return {
          ...prev,
          [key]: data,
        };
      });
    }
  };

  return (
    <>
      <s.ResellerSignupWrapper>
        <s.SignupInputWrapper>
          <s.InputWrapper>
            <InputItem
              label="First Name"
              validateInputMsg={props.validateInputMsg}
              onChangeUserInfo={onChangeResellerInfo}
              userInfo={userInfo}
              fieldKey="firstName"
              type={SIGNUP_UPDATE_TYPE.FIRST_NAME}
              tabIndex={2}
              maxLength={100}
            />

            <InputItem
              label="Last Name"
              validateInputMsg={props.validateInputMsg}
              onChangeUserInfo={onChangeResellerInfo}
              userInfo={userInfo}
              fieldKey="lastName"
              type={SIGNUP_UPDATE_TYPE.LAST_NAME}
              tabIndex={3}
              maxLength={100}
            />

            {step === SIGNUP_STEP.RESELLER_SIGNUP && (
              <>
                <Password
                  ref={passwordRef}
                  validateInputMsg={validateInputMsg}
                  onChangeUserInfo={onChangeResellerInfo}
                  userInfo={userInfo}
                  label="Password"
                  tabIndex={3}
                  fieldKey="password"
                  type={SIGNUP_UPDATE_TYPE.PASSWORD}
                />

                <Password
                  ref={confirmPasswordRef}
                  validateInputMsg={validateInputMsg}
                  onChangeUserInfo={onChangeResellerInfo}
                  userInfo={userInfo}
                  label="Confirm Password"
                  tabIndex={4}
                  fieldKey="confirmPassword"
                  type={SIGNUP_UPDATE_TYPE.CONFIRM_PASSWORD}
                  showRules={false}
                />
              </>
            )}

            <OrgName
              label="Company Name"
              validateInputMsg={props.validateInputMsg}
              onChangeUserInfo={onChangeResellerInfo}
              userInfo={userInfo}
              fieldKey="companyName"
              type={SIGNUP_UPDATE_TYPE.ORG_NAME}
              tabIndex={5}
            />

            <Phone
              validateInputMsg={validateInputMsg}
              onChangeUserInfo={onChangeResellerInfo}
              userInfo={userInfo}
              isPhoneVerified={isPhoneVerified}
              mfaNumber={mfaNumber}
              onClickResend={onClickResend}
              onSendVerify={onSendVerify}
              onClickVerify={onClickVerify}
              onClickVerifyCancel={onClickVerifyCancel}
              verifyCode={verifyCode}
              isValidVerify={isValidVerify}
              requiredVerify={step === SIGNUP_STEP.RESELLER_SIGNUP}
              onChangeVerifyCode={onChangeVerifyCode}
              tabIndex={6}
              essential={step === SIGNUP_STEP.RESELLER_SIGNUP}
            />

            {step === SIGNUP_STEP.RESELLER_SIGNUP && (
              <s.AgreementWrapperContainer>
                <Agreement
                  onChangeUserInfo={onChangeResellerInfo}
                  userInfo={userInfo}
                  fieldKey="agreement"
                  type={SIGNUP_UPDATE_TYPE.AGREEMENT}
                  termsContent={<EULAModal />}
                />

                <Agreement
                  onChangeUserInfo={onChangeResellerInfo}
                  userInfo={userInfo}
                  fieldKey="slaAgreed"
                  label="SLA for myDW"
                  termsTitle="myDW Service Level Agreement"
                  termsContent={<SLAModal />}
                  type={SIGNUP_UPDATE_TYPE.SLA_AGREED}
                />
              </s.AgreementWrapperContainer>
            )}
          </s.InputWrapper>
          <cs.Bottom>
            {validateMsg !== "" && (
              <ValidateWord data-testid="auth-signup-reseller-validate">
                {validateMsg}
              </ValidateWord>
            )}
          </cs.Bottom>
          <s.SubmitWrapper>
            <BigButton
              id="dw_signup_button_signup"
              onClickBtn={onClickSignup}
              label={
                step === SIGNUP_STEP.RESELLER_SIGNUP ? "Sign Up" : "Submit"
              }
              data-testid="auth-signup-step-button-signup-click-signup"
            />
          </s.SubmitWrapper>
        </s.SignupInputWrapper>
        <cs.Bottom center style={{ marginTop: 20 }}>
          <UnderlineText
            id="dw_signup_button_forward_login"
            fontSize={14}
            color={COLORS.BUTTONS1}
            onClick={onClickBackLogin}
            data-testid="auth-signup-step-forward-login-click-back-login"
          >
            Go Back
          </UnderlineText>
        </cs.Bottom>
      </s.ResellerSignupWrapper>
    </>
  );
}
